
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Email subject and body for "Book a Demo" button
  const emailSubject = language === 'en' ? 'Request for Demo' : 'Demande de démonstration';
  const emailBody = language === 'en' ? 'Hello, I would like to book a demo of your services.' : 'Bonjour, je souhaiterais programmer une démonstration de vos services.';
  const mailtoLink = `mailto:info@attaboyintelligence.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <div className="h-10 w-10 relative">
            <svg
              className="h-full w-full text-attaboy"
              viewBox="0 0 200 200"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Updated SVG path to match the correct logo */}
              <path d="M160 40 A40 40 0 1 1 160 120 A40 40 0 1 1 160 40 Z" />
              <path d="M100 50 L40 170 L160 170 Z" />
            </svg>
          </div>
          <div className="ml-2">
            <div className="flex flex-col">
              <span className="font-poppins text-attaboy-black text-xl font-bold tracking-tight">
                Attaboy
              </span>
              <span className="font-poppins text-attaboy text-sm font-medium tracking-tight -mt-1">
                {language === 'en' ? 'Intelligence' : 'Intelligence Artificielle'}
              </span>
            </div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          <Link to="/" className="text-attaboy-black hover:text-attaboy transition-colors">
            {t('nav.home')}
          </Link>
          <Link to="/products" className="text-attaboy-black hover:text-attaboy transition-colors">
            {t('nav.products')}
          </Link>
          <Link to="/solutions" className="text-attaboy-black hover:text-attaboy transition-colors">
            {t('nav.solutions')}
          </Link>
          <Link to="/about" className="text-attaboy-black hover:text-attaboy transition-colors">
            {t('nav.about')}
          </Link>
          <Link to="/contact" className="text-attaboy-black hover:text-attaboy transition-colors">
            {t('nav.contact')}
          </Link>

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center h-10 w-16 bg-gray-100 rounded-full p-1 hover:bg-attaboy-faded transition-colors"
            aria-label={language === 'en' ? 'Switch to French' : 'Switch to English'}
          >
            <span
              className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-all duration-200 ${
                language === 'en'
                  ? 'bg-white text-attaboy-black shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              EN
            </span>
            <span
              className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-all duration-200 ${
                language === 'fr'
                  ? 'bg-white text-attaboy-black shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              FR
            </span>
          </button>

          <Button 
            className="cta-button"
            asChild
          >
            <a href={mailtoLink}>{t('hero.cta.demo')}</a>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center space-x-4">
          {/* Language Toggle (Mobile) */}
          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center h-10 w-16 bg-gray-100 rounded-full p-1"
            aria-label={language === 'en' ? 'Switch to French' : 'Switch to English'}
          >
            <span
              className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-all duration-200 ${
                language === 'en'
                  ? 'bg-white text-attaboy-black shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              EN
            </span>
            <span
              className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-all duration-200 ${
                language === 'fr'
                  ? 'bg-white text-attaboy-black shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              FR
            </span>
          </button>
          
          <button
            onClick={toggleMobileMenu}
            className="text-attaboy-black p-2"
            aria-label="Toggle mobile menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg absolute top-full left-0 w-full p-4 flex flex-col space-y-4 animate-fade-in">
          <Link
            to="/"
            className="text-attaboy-black hover:text-attaboy py-2 px-4 transition-colors"
            onClick={toggleMobileMenu}
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/products"
            className="text-attaboy-black hover:text-attaboy py-2 px-4 transition-colors"
            onClick={toggleMobileMenu}
          >
            {t('nav.products')}
          </Link>
          <Link
            to="/solutions"
            className="text-attaboy-black hover:text-attaboy py-2 px-4 transition-colors"
            onClick={toggleMobileMenu}
          >
            {t('nav.solutions')}
          </Link>
          <Link
            to="/about"
            className="text-attaboy-black hover:text-attaboy py-2 px-4 transition-colors"
            onClick={toggleMobileMenu}
          >
            {t('nav.about')}
          </Link>
          <Link
            to="/contact"
            className="text-attaboy-black hover:text-attaboy py-2 px-4 transition-colors"
            onClick={toggleMobileMenu}
          >
            {t('nav.contact')}
          </Link>
          <Button
            className="cta-button w-full"
            asChild
            onClick={toggleMobileMenu}
          >
            <a href={mailtoLink}>{t('hero.cta.demo')}</a>
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
